/**
 * usePlatformAdmin — is the person using this screen a platform admin?
 *
 * Structure belongs to the admin (/inikru-admin); content belongs to the
 * designer. This hook decides which controls a screen shows. The SERVER is what
 * actually enforces the split — see products/utils/editing_rights.py — because
 * hiding a button restricts nothing on its own. This is the courtesy layer:
 * a designer should not see controls that would only fail.
 *
 * `acting_admin` is the claim an impersonation token carries. Without honouring
 * it, an admin who opened a client's store from the admin panel would see the
 * designer's reduced editor and could never do the structural work these
 * powers exist for.
 */
import { useContext, useMemo } from 'react';
import AuthContext from '../context/AuthContext';

/** Read a claim straight off the access token. */
const claim = (token, key) => {
  try {
    const payload = JSON.parse(atob(String(token).split('.')[1]));
    return payload?.[key];
  } catch {
    return undefined;
  }
};

export const usePlatformAdmin = () => {
  const { user, authToken } = useContext(AuthContext);
  return useMemo(() => {
    if (user?.is_superuser) return true;
    const access = authToken?.access;
    if (!access) return false;
    // is_superuser is also read from the token: `user` can be a slimmed-down
    // object depending on which screen populated it.
    return Boolean(claim(access, 'acting_admin') || claim(access, 'is_superuser'));
  }, [user, authToken]);
};

export default usePlatformAdmin;
